<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import TransitionHeight from "~/components/Common/TransitionHeight.vue";

const props = defineProps<{
  modelValue?: boolean;
  error?: boolean;
  showDeleteAction?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "delete"): void;
}>();

const internalOpen = ref(false);

const isOpen = computed({
  get: () => (props.modelValue !== undefined ? props.modelValue : internalOpen.value),
  set: (value: boolean) => {
    if (props.error) return;
    if (props.modelValue !== undefined) {
      emit("update:modelValue", value);
    } else {
      internalOpen.value = value;
    }
  },
});

function toggleCollapse() {
  if (isOpen.value && props.error) return;
  isOpen.value = !isOpen.value;
}

function close() {
  if (props.error) return;
  isOpen.value = false;
}

function open() {
  if (props.error) return;
  isOpen.value = true;
}

defineExpose({ close, open });
</script>
<template>
  <div>
    <div class="collapse-header" :class="!isOpen ? 'border-b border-stroke-color pb-2' : ''" @click="toggleCollapse">
      <slot name="header"></slot>
      <div class="flex items-center gap-4">
        <SvgIcon v-if="!isOpen" class="w-[16px] h-[16px] text-secondary-color" name="arrow_down"></SvgIcon>
        <SvgIcon v-else class="w-[16px] h-[16px] text-secondary-color" name="arrow_up"></SvgIcon>
        <SvgIcon
          v-if="showDeleteAction"
          name="delete_trash"
          class="w-4 h-4 text-secondary-color"
          @click.stop="emit('delete')"
        />
      </div>
    </div>

    <TransitionHeight :duration="300">
      <div v-if="isOpen" class="border-b border-stroke-color pb-3">
        <slot name="content"></slot>
      </div>
    </TransitionHeight>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";

.collapse-header {
  @apply body_med flex justify-between items-center cursor-pointer;
}
</style>
